import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { StayCompliantNavItem } from "../../../../../src/js/common/service/server/Types";
import {
  BookOpenIcon,
  ClipboardDocumentCheckIcon,
  FolderIcon,
  HomeIcon,
  InboxIcon,
  MagnifyingGlassIcon,
  MapIcon,
  ShieldCheckIcon,
  SignalIcon,
  Square3Stack3DIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { NavItem } from "../../../../../src/js/polaris/components/layout/nav/config";
import _ from "lodash";
import { UserNavItem } from "../../../../../src/js/polaris/components/layout/nav/UserNavItem";
import { OrganizationDto } from "../../../../../src/js/common/service/server/dto/OrganizationDto";
import {
  CUSTOM_ACTIVE_INCOMPLETE_NAV_ITEMS,
  CUSTOM_INACTIVE_INCOMPLETE_NAV_ITEMS,
} from "./NavIcons";

export function BaseNavRedesign({
  navContent,
  settingsRoute,
  userProfileRoute,
}: {
  navContent: React.ReactNode;
  settingsRoute: string;
  userProfileRoute: string;
}) {
  return (
    // Need to use inline styles instead of tailwind since we are inter-mixing previous UI components which have styling conflicts with tailwind
    <div
      style={{
        width: "256px",
        paddingBottom: "26px",
        background: "#f9f8fb",
        display: "flex",
        height: "100%",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          paddingTop: "64px",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <img src="/assets/images/truevault-logo-print.png" width="150px" height="33px" />
      </div>

      <div
        style={{
          paddingBottom: "8px",
          paddingLeft: "8px",
          paddingRight: "8px",
          flexGrow: 1,
          marginTop: "20px",
          overflowY: "auto",
        }}
      >
        {navContent}
      </div>

      <div style={{ paddingLeft: "8px", paddingRight: "8px" }}>
        <hr className="primary-nav__divider mb-[26px]" />
        <UserNavItem
          useNewDesign
          settingsRoute={settingsRoute}
          userProfileRoute={userProfileRoute}
        />
      </div>
    </div>
  );
}

const incompleteBadge = (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.5" y="0.5" width="11" height="11" rx="5.5" fill="#FFBA09" />
    <rect x="0.5" y="0.5" width="11" height="11" rx="5.5" stroke="#F3F0FE" />
  </svg>
);

/**
 * Navigation items can be hidden either by setting `hidden` to `true` or by providing a function that evaluates the org
 * for whether a particular item should be hidden.
 *
 * @param navItem The navigation item for which to evaluate visibility.
 * @param org Organization configuration to inform visibility for the navigation item.
 */
const isNavItemHiddenForOrg = (navItem: NavItem, org: OrganizationDto): boolean => {
  return typeof navItem.hidden === "function"
    ? navItem.hidden(org)
    : typeof navItem.hidden == "undefined"
    ? false
    : navItem.hidden;
};

const NAV_ITEMS_TO_ICONS: Partial<Record<StayCompliantNavItem, React.ReactElement>> = {
  DASHBOARD: <HomeIcon />,
  REQUEST_INBOX: <InboxIcon />,
  RECORD_LOOKUP: <MagnifyingGlassIcon />,
  TASK_LIST: <ClipboardDocumentCheckIcon />,
  DATA_MAP_DASHBOARD: <MapIcon />,
  DOCUMENTS: <FolderIcon />,
  KNOWLEDGE_CENTER: <BookOpenIcon />,
  HR_CENTER: <UserGroupIcon />,
  PRIVACY_CENTER: <ShieldCheckIcon />,
  WORKFLOWS: <Square3Stack3DIcon />,
  EVENT_PULSE: <SignalIcon />,
};

const COLLAPSED_CARET_UNSELECTED_ICON = (
  <svg width="8" height="9" viewBox="0 0 8 9" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 0.613281L8 4.61328L0 8.61328V0.613281Z" fill="#918CA1" />
  </svg>
);

const COLLAPSED_CARET_SELECTED_ICON = (
  <svg width="8" height="9" viewBox="0 0 8 9" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 0.613281L8 4.61328L0 8.61328V0.613281Z" fill="#5331C6" />
  </svg>
);

type SidebarNavLinkRedesignProps = {
  navItem: NavItem;
  org: OrganizationDto;
  isDisabled?: boolean;
  notificationCount?: number;
  showExpandIcon?: boolean;
  onExpandClick?: (e: React.MouseEvent) => void;
  refresh?: () => void;
};

export function SidebarNavLinkRedesign({
  isDisabled,
  navItem,
  org,
  notificationCount = 0,
  refresh,
}: SidebarNavLinkRedesignProps) {
  if (navItem.children && navItem.children?.length > 0) {
    return (
      <SidebarNavLinkWithChildrenRedesign
        isDisabled={isDisabled}
        navItem={navItem}
        org={org}
        notificationCount={notificationCount}
        refresh={refresh}
      />
    );
  } else {
    return (
      <SidebarNavLinkBaseRedesign
        isDisabled={isDisabled}
        navItem={navItem}
        org={org}
        notificationCount={notificationCount}
        refresh={refresh}
      />
    );
  }
}

function SidebarNavLinkBaseRedesign({
  isDisabled,
  navItem,
  notificationCount = 0,
  refresh,
  org,
  showExpandIcon,
  isExpanded, // Only used when showExpandIcon is true
  isChild,
  onExpandClick,
}: SidebarNavLinkRedesignProps & { isChild?: boolean; isExpanded?: boolean }) {
  const location = useLocation();

  let currentPath = location.pathname;
  if (currentPath.endsWith("/")) {
    currentPath = currentPath.slice(0, -1);
  }

  let routeStr = _.isFunction(navItem.route) ? navItem.route(org.id) : navItem.route;

  const isHidden = isNavItemHiddenForOrg(navItem, org);

  if (isHidden) {
    return null;
  }

  let isActive =
    [routeStr, ...(navItem.itemAliases || [])].filter((r) => currentPath == r).length > 0;
  if (navItem.matchRouteOnPrefix && routeStr) {
    isActive = isActive || currentPath.startsWith(routeStr);
  }
  const bgColor = isActive ? "bg-primary-100" : isDisabled ? "" : "hover:bg-primary-100";
  const textColor = isActive
    ? "text-primary-600"
    : isDisabled
    ? "text-neutral-400"
    : "text-neutral-700";
  const hoverTextColor = isActive || isDisabled ? "" : "hover:text-primary-600";

  let icon = NAV_ITEMS_TO_ICONS[navItem.item];

  // Check for a custom incomplete icon
  let hasCustomIncompleteIcon =
    navItem.item in CUSTOM_ACTIVE_INCOMPLETE_NAV_ITEMS &&
    navItem.item in CUSTOM_INACTIVE_INCOMPLETE_NAV_ITEMS;

  if (navItem.showIncompleteBadge === true && hasCustomIncompleteIcon && !isDisabled) {
    icon = isActive
      ? CUSTOM_ACTIVE_INCOMPLETE_NAV_ITEMS[navItem.item]
      : CUSTOM_INACTIVE_INCOMPLETE_NAV_ITEMS[navItem.item];
  }

  if (icon) {
    let iconSize = "w-5 h-5";
    icon = React.cloneElement(icon, { className: `${iconSize}` });
  }

  const labelNode = (
    <div className={`flex items-center`}>
      {navItem.showIncompleteBadge === true && !hasCustomIncompleteIcon && (
        <div style={{ marginLeft: "-32px", position: "absolute" }}>{incompleteBadge}</div>
      )}
      <div className={`flex items-center justify-between w-full`}>
        <div className="flex items-center">
          {icon && <div className="mr-[12px]">{icon}</div>}
          <span className={`text-[14px] font-medium`}>{navItem.label}</span>
        </div>
      </div>
    </div>
  );

  let resultElem: React.ReactNode;
  if (showExpandIcon) {
    resultElem = (
      <div className="flex items-center justify-between">
        {labelNode}
        <div
          onClick={onExpandClick}
          className={`cursor-pointer p-1`}
          style={{
            transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
            transition: "transform 300ms ease-in-out",
          }}
        >
          {isActive ? COLLAPSED_CARET_SELECTED_ICON : COLLAPSED_CARET_UNSELECTED_ICON}
        </div>
      </div>
    );
  } else if (notificationCount > 0) {
    resultElem = (
      <div className="flex items-center justify-between">
        {labelNode}
        <div className="font-medium text-[12px] leading-[20px] px-[12px] py-[2px] bg-white rounded-[10px]">
          {notificationCount}
        </div>
      </div>
    );
  } else {
    resultElem = <div>{labelNode}</div>;
  }

  let padding = isChild ? "pl-[60px]" : "pl-[8px]";
  const itemClasses = `relative block py-[8px] pr-[12px] ${padding} ${bgColor} ${textColor} ${hoverTextColor} rounded-[6px]`;
  return (
    <div className="mb-[4px]">
      {routeStr && !isDisabled ? (
        <NavLink to={routeStr} onClick={refresh} className={itemClasses}>
          {resultElem}
        </NavLink>
      ) : (
        <div className={itemClasses}>{resultElem}</div>
      )}
    </div>
  );
}

function SidebarNavLinkWithChildrenRedesign({
  isDisabled,
  navItem,
  notificationCount = 0,
  refresh,
  org,
}: SidebarNavLinkRedesignProps) {
  const location = useLocation();

  let currentPath = location.pathname;
  if (currentPath.endsWith("/")) {
    currentPath = currentPath.slice(0, -1);
  }

  let routeStr = _.isFunction(navItem.route) ? navItem.route(org.id) : navItem.route;
  const isHidden = isNavItemHiddenForOrg(navItem, org);
  const isActive =
    [routeStr, ...(navItem.itemAliases || [])].filter((r) => currentPath == r).length > 0;
  const isAnyChildActive = navItem.children?.some((childNavItem) => {
    let childRouteStr = _.isFunction(childNavItem.route)
      ? childNavItem.route(org.id)
      : childNavItem.route;
    let isChildActive =
      [childRouteStr, ...(childNavItem.itemAliases || [])].filter((r) => currentPath == r).length >
      0;
    if (childNavItem.matchRouteOnPrefix && childRouteStr) {
      isChildActive = isChildActive || currentPath.startsWith(childRouteStr);
    }
    return isChildActive;
  });

  const [isExpanded, setIsExpanded] = useState(isActive || isAnyChildActive);

  if (isHidden) {
    return null;
  }

  return (
    <div>
      <SidebarNavLinkBaseRedesign
        isDisabled={isDisabled}
        navItem={navItem}
        org={org}
        notificationCount={notificationCount}
        refresh={refresh}
        showExpandIcon={true}
        isExpanded={isExpanded}
        onExpandClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setIsExpanded((s) => !s);
        }}
      />

      <div
        style={{
          display: "grid",
          gridTemplateRows: isExpanded ? "1fr" : "0fr",
          transition: "grid-template-rows 300ms ease-in-out",
        }}
      >
        <div style={{ overflow: "hidden" }}>
          {navItem.children?.map((childNavItem) => {
            return (
              <SidebarNavLinkBaseRedesign
                key={childNavItem.item}
                navItem={childNavItem}
                org={org}
                refresh={refresh}
                isChild={true}
                isDisabled={isDisabled || childNavItem.isDisabled === true}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
